import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AdminConsentRequestPolicy, DirectoryRole, Group, User } from '@microsoft/microsoft-graph-types-beta';
import { select, Store } from '@ngrx/store';
import { combineLatest, filter, map, skipUntil, take } from 'rxjs';
import {
    AdminConsentPolicySchema,
    generateAdminConsentPolicySchema,
} from '../../../../../tools/schemas/graph/admin-consent-policy.schema';
import { BaseComponent } from '../../base/base.component';
import { ManageItemsWrapperDialogComponent } from '../conditional-access-policy/shared/manage-items-wrapper-dialog/manage-items-wrapper-dialog.component';
import { client } from 'projects/angular-clarity/src/app/stores/client';
interface Config {
    title: string;
    allListTitle: string;
    desiredListTitle: string;
    allListComparativeTitle: string;
    type: string;
    // 'includeRoles' | 'includeUsers' | 'includeGroups';
}
const includeRolesDialogConfig = {
    title: 'Roles',
    allListTitle: 'Available roles',
    desiredListTitle: 'Included roles in baseline',
    allListComparativeTitle: 'Current tenant roles',
    type: 'includeRoles',
};

const includeUsersDialogConfig = {
    title: 'Users',
    allListTitle: 'Available users',
    desiredListTitle: 'Included users in baseline',
    allListComparativeTitle: 'Current tenant users',
    type: 'includeUsers',
};

const includeGroupsDialogConfig = {
    title: 'Groups',
    allListTitle: 'Available groups',
    desiredListTitle: 'Included groups in baseline',
    allListComparativeTitle: 'Current tenant groups',
    type: 'includeGroups',
};

@Component({
    selector: 'app-baseline',
    templateUrl: './baseline.component.html',
    styleUrls: ['./baseline.component.scss', '../../styles/baseline-style.scss'],
})
export class BaselineComponent extends BaseComponent implements OnInit {
    groupsLookup: Group[] = [];
    usersLookup: User[] = [];
    rolesLookup: DirectoryRole[] = [];
    currentUsers = [];
    currentGroups = [];
    currentRoles = [];
    data: AdminConsentRequestPolicy;
    mappedReviewers: { type: string; displayName: string; id: string }[] = [];
    override: string[] = [];
    constructor(
        private readonly fb: FormBuilder,
        private store: Store<any>,
        private dialog: MatDialog,
    ) {
        super();
    }

    ngOnInit(): void {
        this.override = this.baselineTemplateItem?.override || [];
        this.getLookups();
        const schema: AdminConsentPolicySchema = JSON.parse(JSON.stringify(this.baseline.schema));
        this.form = this.initForm(schema);
        this.observeForm();
    }

    mapReviewersToNames() {
        this.mappedReviewers = this.data.reviewers.map((reviewer) => {
            const query = reviewer.query;
            let displayName = '';
            let type = '';
            let id = '';

            if (query.includes('/users/')) {
                id = query.split('/users/')[1];
                const user = this.usersLookup.find((u) => u.id === id);
                displayName = user?.displayName ?? id;
                type = 'User';
            } else if (query.includes('/groups/')) {
                id = query.split('/groups/')[1].split('/')[0];
                const group = this.groupsLookup.find((g) => g.id === id);
                displayName = group?.displayName ?? id;
                type = 'Group';
            } else if (query.includes('roleDefinitionId')) {
                const match = query.match(/roleDefinitionId eq '(.+?)'/);
                id = match ? match[1] : '';
                const role = this.rolesLookup.find((r) => r.id === id);
                displayName = role?.displayName ?? id;
                type = 'Role';
            }

            return { id, displayName, type };
        });

        this.currentUsers = this.mappedReviewers.filter((r) => r.type === 'User');
        this.currentGroups = this.mappedReviewers.filter((r) => r.type === 'Group');
        this.currentRoles = this.mappedReviewers.filter((r) => r.type === 'Role');
    }

    private getLookups() {
        const groups_loaded$ = this.store.pipe(
            select(client(this.tenant_id).graph.groups.status),
            map((res) => res.loaded),
            filter((res) => res),
        );

        const groups$ = this.store
            .pipe(select(client(this.tenant_id).graph.groups.all))
            .pipe(skipUntil(groups_loaded$));

        const users_loaded$ = this.store.pipe(
            select(client(this.tenant_id).graph.users.status),
            map((res) => res.loaded),
            filter((res) => res),
        );
        const users$ = this.store.pipe(select(client(this.tenant_id).graph.users.all)).pipe(skipUntil(users_loaded$));

        const roles_loaded$ = this.store.pipe(
            select(client(this.tenant_id).graph.directoryRole.status),
            map((res) => res.loaded),
            filter((res) => res),
        );
        const roles$ = this.store
            .pipe(select(client(this.tenant_id).graph.directoryRole.all))
            .pipe(skipUntil(roles_loaded$));

        const adminConsentPolicyStatus$ = this.store.pipe(
            select(client(this.tenant_id).graph.adminConsentRequest.status),
            map((res) => res.loaded),
            filter((res) => res),
        );
        const adminConsentPolicy$ = this.store
            .pipe(select(client(this.tenant_id).graph.adminConsentRequest.item))
            .pipe(skipUntil(adminConsentPolicyStatus$));

        combineLatest([groups$, users$, roles$, adminConsentPolicy$]).subscribe(([groups, users, roles]) => {
            this.groupsLookup = groups;
            this.usersLookup = users;
            this.rolesLookup = roles;
            this.mapReviewersToNames();
        });
    }

    observeForm(): void {
        this.form.valueChanges.subscribe((res) => {
            const schema = generateAdminConsentPolicySchema(res);

            if (this.form.get('requestDurationInDays').errors) {
                this.baselineErrorChange.next({
                    remediate: true,
                    save: true,
                });
            } else {
                this.baselineErrorChange.next({
                    remediate: false,
                    save: false,
                });
            }
            this.baselineChange.next({
                ...this.baseline,
                schema: JSON.parse(JSON.stringify(schema)),
            });
        });

        this.form.get('isEnabled').valueChanges.subscribe((res) => {
            this.form.get('notifyReviewers').setValue(res);
            this.form.get('remindersEnabled').setValue(res);
            this.form.get('requestDurationInDays').setValue(10);
        });
    }

    initForm(schema) {
        const patterns: string[] =
            schema.properties?.reviewers?.allOf?.map((res) => res['contains']?.properties.query.pattern) || [];

        return this.fb.group({
            isEnabled: [schema.properties.isEnabled.const],
            notifyReviewers: [schema.properties?.notifyReviewers?.const],
            remindersEnabled: [schema.properties?.remindersEnabled?.const],
            requestDurationInDays: [
                schema.properties?.requestDurationInDays?.const,
                [Validators.min(1), Validators.max(60)],
            ],

            includeUsers: [
                patterns?.filter((res) => res?.includes('users'))?.map((res) => res.replace('users/', '')) || [],
            ],
            includeGroups: [
                patterns?.filter((res) => res?.includes('groups'))?.map((res) => res.replace('groups/', '')) || [],
            ],

            includeRoles: [patterns?.filter((res) => !res?.includes('users') && !res.includes('groups')) || []],
        });
    }

    manageItems(type: 'includeUsers' | 'includeGroups' | 'includeRoles') {
        let lookup: any = this.usersLookup;
        if (type === 'includeGroups') {
            lookup = this.groupsLookup;
        } else if (type === 'includeRoles') {
            lookup = this.rolesLookup;
        }

        let config: Config = includeUsersDialogConfig;

        if (type === 'includeGroups') {
            config = includeGroupsDialogConfig;
        } else if (type === 'includeRoles') {
            config = includeRolesDialogConfig;
        }

        const allItems = lookup.map((res) => {
            return {
                displayName: res.displayName,
                id: res.id,
                inBaselineSchema: this.form.value[type].includes(res.id),
                inPolicy: true,
                //this.data.reviewers[type].includes(res.id),
            };
        });

        const dialogRef = this.dialog.open(ManageItemsWrapperDialogComponent, {
            data: {
                allItems: allItems,
                ...config,
            },
            maxHeight: '90vh',
            width: '70%',
        });

        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((res) => {
                if (res instanceof Array) {
                    console.log(res);
                    const schemaItems = res.filter((res) => res.inBaselineSchema) || [];

                    this.form.get(type).setValue(schemaItems.map((res) => res.id));
                }
            });
    }

    get formValue() {
        return this.form.value as AdminConsentRequestPolicy;
    }
}
